import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { buildFormData, buildQueryString } from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { PagingArgument } from 'src/lib/utilities/api/patterns/pagination/paging.argument';
import {
	StudentUpdateHistoryResponseModel,
	StudentUpdateHistorySearchArgument,
	UpdateUserRequestArgumentResponse,
	UserApproveRequestArgument,
	UserUpdateRequestArgument,
} from './users-update-request.argument';
import {
	UsersPendingRequestsResponse,
	UsersRequestOptionsResponse,
} from './users-update-request.model';

@Injectable({
	providedIn: 'root',
})
export class UsersUpdateRequestsService {
	constructor(private httpClient: HttpClient) {}

	public getRequestsOptions = (
		uid: number,
	): Observable<UsersRequestOptionsResponse> => {
		return this.httpClient
			.get<UsersRequestOptionsResponse>(
				`api/v1/user/${uid}/update_request/options`,
			)
			.pipe(
				mapResponse((r) => plainToInstance(UsersRequestOptionsResponse, r), {
					errorCode: 'F1D58089',
					checkPreMap: false,
					validateSuccess: false,
				}),
			);
	};

	public getPendingRequests = (
		uid: number,
	): Observable<UsersPendingRequestsResponse> => {
		return this.httpClient
			.get<UsersPendingRequestsResponse>(
				`api/v1/user/${uid}/update_request/pending`,
			)
			.pipe(
				mapResponse((r) => plainToInstance(UsersPendingRequestsResponse, r), {
					errorCode: '87E055A7',
					checkPreMap: false,
					validateSuccess: false,
				}),
			);
	};

	public createUpdateRequest = (
		uid: number,
		argument: UserUpdateRequestArgument,
		proofFiles: {
			ids: string[];
			file: File;
		}[] = [],
		canBypassProofRequirement: boolean = false,
	) => {
		const bodyArgs = {};
		bodyArgs['jsonBody'] = argument;

		const proofs: { key: string; alias: string }[] = [];

		if (proofFiles?.length > 0) {
			proofFiles.forEach((proof, index) => {
				const alias = `proof_${index}`;
				if (proof.ids?.length > 1) {
					proof.ids.forEach((id) => {
						proofs.push({ key: id, alias: alias });
					});
					bodyArgs[alias] = proof.file;
				} else {
					const p = proof.ids[0];
					bodyArgs[p] = proof.file;
				}
			});
		}

		if (proofs.length > 0) {
			bodyArgs['proof_mappings'] = proofs;
		}

		const formData = buildFormData(bodyArgs);

		const qs = buildQueryString({
			bypass_proof: canBypassProofRequirement,
		});

		return this.httpClient
			.post<UpdateUserRequestArgumentResponse>(
				`/api/v1/user/${uid}/update_request/create${qs}`,
				formData,
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(UpdateUserRequestArgumentResponse, r),
					{
						errorCode: 'BBEBC6DA',
						errorMessage: DefaultErrorMessage.Saving,
						checkPreMap: false,
						validateSuccess: false,
					},
				),
			);
	};

	public approveUpdateRequest = (
		uid: number,
		request_id: number,
		argument: UserApproveRequestArgument,
	) => {
		return this.httpClient
			.post<UpdateUserRequestArgumentResponse>(
				`/api/v1/user/${uid}/update_request/${request_id}/approve`,
				argument,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '1BEC1C05',
					errorMessage: DefaultErrorMessage.Saving,
					checkPreMap: false,
					validateSuccess: false,
				}),
			);
	};

	public approveMultipleUpdateRequest = (
		uid: number,
		argument: UserApproveRequestArgument,
	) => {
		return this.httpClient
			.post<UpdateUserRequestArgumentResponse>(
				`/api/v1/user/${uid}/update_request/approve`,
				argument,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'EEF91DD3',
					errorMessage: DefaultErrorMessage.Saving,
					checkPreMap: false,
					validateSuccess: false,
				}),
			);
	};

	public getUpdateHistory = (
		uid: number,
		args: PagingArgument<StudentUpdateHistorySearchArgument>,
	) => {
		return this.httpClient
			.post<StudentUpdateHistoryResponseModel>(
				`/api/v1/user/${uid}/update_request/fields/history`,
				args,
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(StudentUpdateHistoryResponseModel, r),
					{
						errorCode: 'E748CC44',
						checkPreMap: false,
						validateSuccess: false,
					},
				),
			);
	};
}
